// Imported here, not in SubmissionStatus, because SagaPill is consumed on its own by the
// overview card — importing it there would leave the pill unstyled on that surface.
import './style.scss';

import { useState } from 'react';

import type { ISagaStep, ISagaView, SagaVerdict, StepState } from '../../../interface/saga-status';
import { TypeButton } from '../../utils/button';
import { CopyValue } from '../../utils/copy';
import { Hvc } from '../../utils/hooks';

/**
 * Every state is carried by four redundant channels — glyph shape, a word, an accessible
 * label, and colour last. A greyscale screenshot of this component must remain fully readable,
 * which is also what makes it work for a colour-blind agent and on a washed-out phone screen
 * in daylight.
 */
const STEP_GLYPH: Record<StepState, string> = {
	done: '✓',
	active: '◷',
	pending: '·',
	failed: '!',
	warn: '✓',
};

/** The word an assistive technology reads, and the visible micro-label. */
const STEP_WORD: Record<StepState, string> = {
	done: 'Done',
	active: 'In progress',
	pending: 'Not started',
	failed: 'Needs attention',
	warn: 'Done with warnings',
};

const VERDICT_GLYPH: Record<SagaVerdict, string> = {
	landed: '✓',
	working: '◷',
	checkPhoto: '!',
	reviewFigures: '!',
	stalled: '✕',
	unknown: '?',
};

const VERDICT_TONE: Record<SagaVerdict, string> = {
	landed: 'ok',
	working: 'working',
	checkPhoto: 'attention',
	reviewFigures: 'attention',
	stalled: 'stalled',
	unknown: 'waiting',
};

export const SagaStepList = ({ steps }: { steps: ISagaStep[] }) => (
	<ol className='saga-steps'>
		{steps.map((step) => (
			<li
				key={step.key}
				className={`saga-steps__item saga-steps__item--${step.state}`}
			>
				<span
					className='saga-steps__glyph'
					aria-hidden='true'
				>
					{STEP_GLYPH[step.state]}
				</span>
				<span className='saga-steps__body'>
					<span className='saga-steps__label'>
						{step.label}
						{/* Bootstrap 5's utility. `.sr-only` is Bootstrap 4 and is not defined here. */}
						<span className='visually-hidden'> — {STEP_WORD[step.state]}</span>
					</span>
					{step.note ? <span className='saga-steps__note'>{step.note}</span> : null}
				</span>
			</li>
		))}
	</ol>
);

/**
 * The post-submission surface: a headline verdict the agent can read in two seconds, with the
 * stage detail folded away underneath.
 *
 * The headline is derived, not hardcoded — it says "Your result is in" as soon as the agent's
 * own obligation is discharged, even while downstream checks are still running, and only
 * becomes a verb when there is genuinely something for them to do.
 */
export const SagaPipelinePanel = ({
	view,
	referenceId,
	onAction,
	onRetryAi,
	canRetryAi,
	retryState,
	isWatching,
	onRefresh,
	isOffline,
}: {
	view: ISagaView;
	referenceId?: string;
	onAction?: (kind: NonNullable<ISagaView['action']>['kind']) => void;
	onRetryAi?: () => void;
	canRetryAi?: boolean;
	retryState?: 'idle' | 'queueing' | 'queued' | 'moved' | 'unchanged' | 'error';
	isWatching?: boolean;
	onRefresh?: () => void;
	isOffline?: boolean;
}) => {
	const [expanded, setExpanded] = useState(view.verdict !== 'landed');

	return (
		<section
			className={`saga-panel saga-panel--${VERDICT_TONE[view.verdict]}`}
			aria-live='polite'
		>
			<div className='saga-panel__head'>
				<span
					className='saga-panel__glyph'
					aria-hidden='true'
				>
					{VERDICT_GLYPH[view.verdict]}
				</span>
				<div className='saga-panel__copy'>
					<h3 className='saga-panel__headline'>{view.headline}</h3>
					<p className='saga-panel__detail'>{view.detail}</p>
					{referenceId ? (
						<p className='saga-panel__ref'>
							<span>Ref</span>
							<CopyValue
								value={referenceId}
								variant='inline'
								display={`${referenceId.slice(0, 8)}…`}
							/>
						</p>
					) : null}
				</div>
			</div>

			<Hvc
				removeDOM
				view={!!isOffline}
			>
				<p className='saga-panel__offline'>You’re offline. We’ll check again when your connection returns.</p>
			</Hvc>

			{/*
				A 202 from the trigger endpoint means the message was accepted for delivery, not
				that anything happened — so this never says "Retried successfully". It says queued,
				then reports what was actually observed on the row.
			*/}
			<Hvc
				removeDOM
				view={retryState === 'queued' || retryState === 'moved' || retryState === 'unchanged' || retryState === 'error'}
			>
				<p className='saga-panel__retry-note'>
					{retryState === 'queued'
						? 'Queued. We’ll update this screen when it finishes.'
						: retryState === 'moved'
							? 'It ran — the status above is up to date.'
							: retryState === 'unchanged'
								? 'It hasn’t started yet. This can take a few minutes on a busy day.'
								: 'Couldn’t queue it — check your connection and try again.'}
				</p>
			</Hvc>

			<div className='saga-panel__actions'>
				{view.action && onAction ? (
					<TypeButton
						title={view.action.label}
						buttonSize='small'
						onClick={() => onAction(view.action!.kind)}
					/>
				) : null}
				{canRetryAi && onRetryAi ? (
					<TypeButton
						title='Try reading it again'
						buttonSize='small'
						buttonType='outlined'
						load={retryState === 'queueing'}
						onClick={onRetryAi}
					/>
				) : null}
				{!isWatching && onRefresh ? (
					<TypeButton
						title='Check again'
						buttonSize='little'
						buttonType='outlined'
						onClick={onRefresh}
					/>
				) : null}
			</div>

			<Hvc
				removeDOM
				view={view.steps.length > 0}
			>
				<div className='saga-panel__detail-block'>
					<button
						type='button'
						className='saga-panel__toggle'
						aria-expanded={expanded}
						onClick={() => setExpanded((open) => !open)}
					>
						What happens next
						<span aria-hidden='true'>{expanded ? '▴' : '▾'}</span>
					</button>
					<Hvc
						removeDOM
						view={expanded}
					>
						<SagaStepList steps={view.steps} />
					</Hvc>
				</div>
			</Hvc>
		</section>
	);
};

/** One-line form for list rows and the overview card. */
export const SagaPill = ({ view }: { view: ISagaView }) => (
	<span className={`saga-pill saga-pill--${VERDICT_TONE[view.verdict]}`}>
		<span aria-hidden='true'>{VERDICT_GLYPH[view.verdict]}</span>
		{view.headline}
	</span>
);
